import CategorySection from './components/CategorySection';
import FooterSection from './components/FooterSection';
import Header from '@/components/feature/Header';
import SchemaOrg from '@/components/base/SchemaOrg';
import { organizationSchema, websiteSchema } from '@/mocks/schema-data';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

export default function Home() {
  const handleGoToMain = () => {
    window.open('https://kaspreadsheet.com', '_blank', 'noopener,nofollow');
  };

  return (
    <div className="min-h-screen bg-background-50">
      <SchemaOrg data={organizationSchema} />
      <SchemaOrg data={websiteSchema} />
      <Header />

      {/* Hero */}
      <section className="relative w-full min-h-[600px] md:min-h-[700px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=A%20bright%20airy%20flat%20lay%20composition%20featuring%20an%20array%20of%20stylish%20modern%20products%20including%20a%20pair%20of%20white%20leather%20sneakers%20a%20soft%20beige%20linen%20scarf%20a%20minimalist%20watch%20a%20leather%20tote%20bag%20and%20a%20neatly%20folded%20cream%20sweater%20arranged%20on%20a%20warm%20cream%20background%20soft%20diffused%20natural%20window%20light%20casting%20gentle%20shadows%20editorial%20catalog%20photography%20clean%20fresh%20aesthetic%20with%20subtle%20sage%20green%20botanical%20accents%20harmonious%20balanced%20composition&width=1600&height=900&seq=sugargoo-hero-v2&orientation=landscape"
            alt="Sugargoo Spreadsheet product discovery background"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/15 to-black/30"></div>
        </div>

        <div className="relative z-10 w-full max-w-4xl mx-auto px-4 md:px-6 text-center py-20">
          <h1 className="text-3xl md:text-5xl lg:text-6xl text-background-50 mb-5 leading-tight">
            Sugargoo Spreadsheet
            <br />
            <span className="text-2xl md:text-4xl lg:text-5xl font-body font-light">
              Find The Best Products Faster
            </span>
          </h1>

          <p className="text-background-50/85 text-sm md:text-base max-w-xl mx-auto mb-8 leading-relaxed">
            The ultimate Sugargoo Spreadsheet resource for discovering trending products, verified finds,
            shopping guides, shipping information and money-saving tips.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4 mb-10">
            <button
              onClick={handleGoToMain}
              className="w-full sm:w-auto bg-primary-500 text-background-50 px-8 py-3.5 rounded-md text-sm font-medium hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              Sugargoo Spreadsheet
            </button>
            <button
              onClick={handleGoToMain}
              className="w-full sm:w-auto bg-background-50/90 text-foreground-900 px-8 py-3.5 rounded-md text-sm font-medium hover:bg-background-50 transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              Buy It Now
            </button>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6">
            {['Updated Daily Resources', 'Organized Product Database', 'Beginner Friendly', 'Shopping Safety Guides'].map((badge) => (
              <span key={badge} className="flex items-center gap-1.5 text-xs text-background-50/80 whitespace-nowrap">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-check-line text-primary-400"></i>
                </span>
                {badge}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Category Section */}
      <CategorySection categories={categories} />

      {/* What Is Sugargoo Spreadsheet */}
      <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100/60">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
            <div className="flex-1">
              <h2 className="text-2xl md:text-4xl text-foreground-950 mb-5">
                What Is Sugargoo Spreadsheet?
              </h2>
              <p className="text-foreground-600 text-sm md:text-base leading-relaxed mb-4">
                A <strong>Sugargoo Spreadsheet</strong> is a structured product discovery database designed to help shoppers
                find products from Chinese marketplaces faster and more efficiently. Instead of spending hours manually
                searching through thousands of product listings, users can explore organized categories, browse verified
                product links, compare prices, and access detailed shopping recommendations — all in one place.
              </p>
              <p className="text-foreground-600 text-sm md:text-base leading-relaxed mb-4">
                Whether you are looking for the latest sneaker releases, trending streetwear pieces, high-quality
                accessories, or budget-friendly finds, a well-maintained <strong>Sugargoo Spreadsheet</strong> saves you
                countless hours of research. Each entry typically includes product links, QC photos, size guides, price
                comparisons, and user reviews — giving you everything you need to shop with confidence.
              </p>
              <p className="text-foreground-600 text-sm md:text-base leading-relaxed">
                Our platform curates and organizes the most comprehensive <strong>Sugargoo Spreadsheet</strong> resources
                available, updated regularly to reflect the latest trending products and community discoveries. Think of it
                as your personal shopping assistant that never sleeps.
              </p>
            </div>
            <div className="flex-1 w-full">
              <div className="relative w-full aspect-[4/3] rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=A%20bright%20modern%20workspace%20desk%20with%20an%20open%20laptop%20showing%20an%20organized%20product%20spreadsheet%20interface%20surrounded%20by%20fashion%20accessories%20including%20a%20leather%20watch%20folded%20cream%20sweater%20and%20white%20sneakers%20soft%20natural%20morning%20light%20streaming%20through%20a%20window%20minimalist%20interior%20with%20warm%20beige%20tones%20sage%20green%20plant%20and%20light%20oak%20desk%20cozy%20productive%20creative%20atmosphere&width=800&height=600&seq=sugargoo-what-v2&orientation=landscape"
                  alt="Modern workspace with Sugargoo Spreadsheet on laptop screen"
                  className="w-full h-full object-cover object-top rounded-lg"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose */}
      <section className="py-14 md:py-20 px-4 md:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-4xl text-foreground-950 mb-3">
              Why Choose Our Sugargoo Spreadsheet?
            </h2>
            <p className="text-foreground-600 text-sm md:text-base max-w-xl mx-auto">
              Compare how our organized database transforms your shopping research experience
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-background-200">
                  <th className="text-left py-4 px-4 md:px-6 text-sm font-medium text-foreground-800 whitespace-nowrap">Feature</th>
                  <th className="text-left py-4 px-4 md:px-6 text-sm font-medium text-primary-700 whitespace-nowrap">
                    <span className="flex items-center gap-1.5">
                      <span className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-star-line"></i>
                      </span>
                      Sugargoo Spreadsheet
                    </span>
                  </th>
                  <th className="text-left py-4 px-4 md:px-6 text-sm font-medium text-foreground-500 whitespace-nowrap">Traditional Searching</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { feature: 'Product Discovery', ours: 'Organized database', theirs: 'Manual searching' },
                  { feature: 'Time Efficiency', ours: 'High efficiency', theirs: 'Slow research' },
                  { feature: 'Categories', ours: 'Multiple collections', theirs: 'Limited' },
                  { feature: 'Product Comparison', ours: 'Easy side-by-side', theirs: 'Difficult' },
                  { feature: 'Beginner Friendly', ours: 'Yes, guided experience', theirs: 'Steep learning curve' },
                  { feature: 'Updates', ours: 'Regularly refreshed', theirs: 'Static, outdated' },
                ].map((row, i) => (
                  <tr key={row.feature} className={`border-b border-background-100 ${i % 2 === 0 ? 'bg-background-50' : 'bg-background-100/40'}`}>
                    <td className="py-3.5 px-4 md:px-6 text-sm text-foreground-700 font-medium whitespace-nowrap">{row.feature}</td>
                    <td className="py-3.5 px-4 md:px-6 text-sm text-primary-700 whitespace-nowrap">
                      <span className="flex items-center gap-1.5">
                        <span className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-check-line text-primary-500"></i>
                        </span>
                        {row.ours}
                      </span>
                    </td>
                    <td className="py-3.5 px-4 md:px-6 text-sm text-foreground-500 whitespace-nowrap">
                      <span className="flex items-center gap-1.5">
                        <span className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-close-line text-foreground-400"></i>
                        </span>
                        {row.theirs}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100/60">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-4xl text-foreground-950 mb-3">
              How Sugargoo Spreadsheet Works
            </h2>
            <p className="text-foreground-600 text-sm md:text-base max-w-xl mx-auto">
              Four simple steps to transform your shopping research
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                step: '01',
                title: 'Browse Categories',
                desc: 'Explore our organized product categories to find exactly what you are looking for — from sneakers to accessories.',
                icon: 'ri-grid-line',
              },
              {
                step: '02',
                title: 'Compare Products',
                desc: 'Review product details, QC photos, size charts and price comparisons side by side before making a decision.',
                icon: 'ri-scales-line',
              },
              {
                step: '03',
                title: 'Read Guides',
                desc: 'Access comprehensive shopping guides, shipping tips and money-saving strategies written by experienced shoppers.',
                icon: 'ri-book-open-line',
              },
              {
                step: '04',
                title: 'Visit Main Website',
                desc: 'Ready to shop? Head over to our main website to start building your haul with confidence.',
                icon: 'ri-external-link-line',
              },
            ].map((item) => (
              <div key={item.step} className="text-center p-5">
                <span className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-primary-100/70 text-primary-600 mb-4">
                  <i className={`${item.icon} text-xl`}></i>
                </span>
                <div className="text-xs font-medium text-primary-500 mb-2 whitespace-nowrap">Step {item.step}</div>
                <h4 className="font-heading text-base text-foreground-900 mb-2">{item.title}</h4>
                <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SEO Article Cards Section */}
      <section id="guides" className="py-14 md:py-20 px-4 md:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-4xl text-foreground-950 mb-3">
              Sugargoo Spreadsheet Guides & Resources
            </h2>
            <p className="text-foreground-600 text-sm md:text-base max-w-xl mx-auto">
              In-depth articles and expert guides to help you master every aspect of Sugargoo shopping
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {articles.map((article) => (
              <a
                key={article.slug}
                href={`/${article.slug}`}
                className="group block bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-primary-300 transition-all duration-300"
              >
                <div className="w-full aspect-[3/2] overflow-hidden">
                  <img
                    src={article.cardImage}
                    alt={article.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-400"
                  />
                </div>
                <div className="p-4 md:p-5">
                  <span className="inline-block text-xs font-medium text-primary-600 bg-primary-50/70 px-2.5 py-1 rounded-full mb-3 whitespace-nowrap">
                    {article.category}
                  </span>
                  <h3 className="font-heading text-base md:text-lg text-foreground-950 mb-2 group-hover:text-primary-600 transition-colors duration-200 leading-snug">
                    {article.title}
                  </h3>
                  <p className="text-xs md:text-sm text-foreground-600 leading-relaxed mb-3 line-clamp-3">
                    {article.excerpt}
                  </p>
                  <span className="flex items-center gap-1.5 text-sm text-primary-600 font-medium whitespace-nowrap">
                    Read Full Guide
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-right-line"></i>
                    </span>
                  </span>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Why Trust */}
      <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100/60">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-2xl md:text-4xl text-foreground-950 mb-3">Why Trust Our Information?</h2>
          <p className="text-foreground-600 text-sm md:text-base max-w-xl mx-auto mb-10">
            We are committed to providing accurate, transparent and helpful shopping resources
          </p>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 md:gap-6">
            {[
              { icon: 'ri-shield-check-line', label: 'Independent Platform' },
              { icon: 'ri-file-list-3-line', label: 'No Direct Selling' },
              { icon: 'ri-book-read-line', label: 'Educational Content' },
              { icon: 'ri-lock-line', label: 'Security Focused' },
              { icon: 'ri-refresh-line', label: 'Regular Updates' },
            ].map((item) => (
              <div key={item.label} className="flex flex-col items-center gap-3 p-5 bg-background-50 border border-background-200/70 rounded-lg">
                <span className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100/70 text-accent-600">
                  <i className={`${item.icon} text-lg`}></i>
                </span>
                <span className="text-xs md:text-sm text-foreground-700 font-medium text-center whitespace-nowrap">
                  {item.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative py-16 md:py-24 px-4 md:px-6 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=A%20warm%20inviting%20abstract%20background%20with%20soft%20gradient%20blending%20of%20cream%20warm%20beige%20and%20subtle%20sage%20green%20tones%20with%20a%20delicate%20linen%20fabric%20texture%20overlay%20organic%20flowing%20shapes%20peaceful%20calm%20minimalist%20aesthetic%20no%20harsh%20lines%20elegant%20soothing%20premium%20feel&width=1600&height=600&seq=sugargoo-cta-v2&orientation=landscape"
            alt="Background decorative texture"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-background-50/60"></div>
        </div>

        <div className="relative z-10 max-w-2xl mx-auto text-center">
          <h2 className="text-2xl md:text-4xl text-foreground-950 mb-4">
            Ready To Discover Better Products?
          </h2>
          <p className="text-foreground-600 text-sm md:text-base mb-8 max-w-lg mx-auto leading-relaxed">
            Explore the Sugargoo Spreadsheet today and simplify your shopping research. Thousands of verified products,
            organized categories, and expert guides are just one click away.
          </p>
          <button
            onClick={handleGoToMain}
            className="bg-primary-500 text-background-50 px-8 py-3.5 rounded-md text-sm font-medium hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap"
          >
            Explore Sugargoo Spreadsheet Now
          </button>
        </div>
      </section>

      <FooterSection />
    </div>
  );
}